/**
 * The one place the strategic-analysis visual language is defined.
 *
 * The approved mockups were authored in `light-dark()` CSS with literal hex.
 * NONE of those literals live in a component here. Every surface below is
 * derived from `useThemeColors()` — the same tokens the rest of the app reads —
 * so a palette change moves these screens with it, and a component can neither
 * invent a colour nor drift from the brand.
 *
 * WHY A HOOK AND NOT A CONSTANT: the Inverio ramps are INVERTED between themes.
 * `primary[900]` is a deep teal in light and a pale mint in dark, so "the deep
 * teal conclusion card" cannot be one token — it is `primary[900]` in light and
 * `primary[50]` in dark. Reading `isDark` once, here, is what keeps that
 * inversion out of five framework components.
 */

import { createContext, useContext } from 'react';
import { useThemeStore } from '@/lib/state/theme-store';
import { useThemeColors } from '@/lib/useThemeColors';

/**
 * Whether there is room for a two-column composition.
 *
 * MEASURED, never `Platform.OS === 'web'`. The web build runs at 390pt in a
 * phone browser and in a 390pt-wide window, and keying the wide layout off the
 * platform put the market comparison chart and the assumptions panel side by
 * side in a 170pt column — every heading truncated, every number wrapped.
 *
 * The threshold is in CONTENT pixels, not window pixels.
 */
export const WIDE_LAYOUT_MIN_WIDTH = 900;

/**
 * The width of the container the analysis actually renders into.
 *
 * WHY NOT `useWindowDimensions`. On web the analysis sits inside `WebAppShell`,
 * behind a 260pt sidebar and 20pt of padding on each side. A 1024pt WINDOW
 * therefore leaves about 724pt of content — and the old check compared 1024
 * against 900, chose the two-column layout, and rendered it into 724pt. The
 * window is not the container, and on this screen it never was.
 *
 * `null` means "nothing has measured yet". It is deliberately NOT a guess at the
 * window size: a guess makes the first paint disagree with the second, which on
 * web is a hydration mismatch and everywhere is a visible reflow. Narrow is the
 * safe unmeasured answer — a single column at a wide width is merely roomy,
 * while two columns at a narrow width is the bug this exists to prevent.
 */
const AnalysisWidthContext = createContext<number | null>(null);

export const AnalysisWidthProvider = AnalysisWidthContext.Provider;

/**
 * The measured content width, or null before the first layout pass.
 *
 * Exposed for components that need the number rather than the verdict.
 */
export function useAnalysisWidth(): number | null {
  return useContext(AnalysisWidthContext);
}

export function useIsWideLayout(): boolean {
  const width = useContext(AnalysisWidthContext);
  return width !== null && width >= WIDE_LAYOUT_MIN_WIDTH;
}

export type EvidenceTone = 'positive' | 'caution' | 'adverse' | 'unknown';

export interface AnalysisVisuals {
  isDark: boolean;
  colors: ReturnType<typeof useThemeColors>;
  /** The conclusion card. Deep brand teal in both themes, by different routes. */
  hero: {
    background: string;
    label: string;
    title: string;
    body: string;
    badgeBackground: string;
    badgeText: string;
  };
  /** The visualisation canvas: plot area, connectors, zone captions. */
  chart: {
    surface: string;
    border: string;
    grid: string;
    axis: string;
    axisLabel: string;
    zoneLabel: string;
  };
  /** A raised element sitting ON the canvas — a node, a force tile. */
  plate: {
    background: string;
    border: string;
    text: string;
    selectedBackground: string;
    selectedBorder: string;
    selectedText: string;
    shadow: {
      shadowColor: string;
      shadowOpacity: number;
      shadowRadius: number;
      shadowOffset: { width: number; height: number };
      elevation: number;
    };
  };
  /** The focused detail card under every visualisation. */
  detail: {
    background: string;
    border: string;
  };
  /** The left-ruled implication strip. */
  implication: {
    background: string;
    rule: string;
    text: string;
  };
  /** The "AI + web research" provenance pill. */
  provenancePill: {
    background: string;
    border: string;
    text: string;
  };
  /** Evidence-strength colouring, shared by every framework's bands. */
  tone: (tone: EvidenceTone) => { fore: string; back: string };
}

/**
 * Classification / posture / tier colouring.
 *
 * `unknown` is deliberately GREY and never green: an insufficient-evidence
 * result that borrows the "fine" colour reads as a mild positive, which is the
 * single most damaging misread this palette could produce.
 */
export function useAnalysisVisuals(): AnalysisVisuals {
  const colors = useThemeColors();
  const isDark = useThemeStore((s) => s.isDark);

  const heroBackground = isDark ? colors.primary[50] : colors.primary[900];
  const heroTitle = isDark ? colors.text.primary : colors.text.inverse;

  return {
    isDark,
    colors,
    hero: {
      background: heroBackground,
      label: isDark ? colors.primary[700] : colors.primary[200],
      title: heroTitle,
      // Body copy stays NEUTRAL on the dark card. The bright end of the teal
      // ramp is an accent; a paragraph set in it competes with the headline
      // above it and reads as another label rather than as prose.
      body: isDark ? colors.text.secondary : colors.primary[100],
      badgeBackground: isDark ? colors.primary[100] : colors.primary[800],
      badgeText: isDark ? colors.primary[800] : colors.primary[100],
    },
    chart: {
      surface: colors.surface.primary,
      border: colors.border.light,
      grid: colors.border.light,
      axis: colors.border.medium,
      axisLabel: colors.text.tertiary,
      zoneLabel: colors.text.tertiary,
    },
    plate: {
      background: colors.surface.primary,
      border: colors.border.medium,
      text: colors.text.primary,
      selectedBackground: isDark ? colors.primary[50] : colors.primary[50],
      selectedBorder: isDark ? colors.primary[600] : colors.primary[600],
      selectedText: isDark ? colors.primary[900] : colors.primary[900],
      shadow: {
        shadowColor: '#000000',
        shadowOpacity: isDark ? 0.4 : 0.12,
        shadowRadius: 8,
        shadowOffset: { width: 0, height: 3 },
        elevation: 3,
      },
    },
    detail: {
      background: colors.surface.primary,
      border: colors.border.light,
    },
    implication: {
      background: isDark ? colors.primary[50] : colors.primary[50],
      rule: colors.primary[600],
      text: colors.text.primary,
    },
    provenancePill: {
      background: colors.primary[50],
      border: isDark ? colors.primary[200] : colors.primary[100],
      text: isDark ? colors.primary[800] : colors.primary[800],
    },
    tone: (tone) => {
      switch (tone) {
        case 'positive':
          return { fore: colors.success, back: colors.traffic.greenLight };
        case 'caution':
          return { fore: colors.amber.main, back: colors.amber.light };
        case 'adverse':
          return { fore: colors.error, back: colors.red.light };
        case 'unknown':
          return { fore: colors.text.tertiary, back: colors.surface.tertiary };
      }
    },
  };
}
